import { useLocation } from 'react-router-dom';

import type { Payable } from 'modules/bookkeep/payables/containers/PayablePanelContainer/hooks/usePayableQuery/payable';
import {
  getIsPayableExported,
  getIsPayablePrepared,
  isSpanishDPRPayable,
} from 'modules/bookkeep/payables/models/payable';
import type {
  Field,
  SelectedFilter,
} from 'src/core/common/components/FilterBuilder';
import { toFilterUrlParameter } from 'src/core/common/components/FilterBuilder/FilterBuilderModel';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import {
  type PayableDateRange,
  extractPayableDateRangeFromPayableFilter,
} from '../../../utils/payableFilterDateRange';

export const useSpanishDprUnexportedPayablesRedirectInfo = (
  payable: Payable,
):
  | {
      i18nKey: string;
      href: string;
    }
  | undefined => {
  const companyId = useCompanyId();
  const { search } = useLocation();

  if (!isSpanishDPRPayable(payable)) {
    return undefined;
  }

  if (getIsPayableExported(payable)) {
    return undefined;
  }

  const searchParams = new URLSearchParams(search);
  const isSpainDprUnexportedRedirect =
    searchParams.get('spainDprUnexportedPayablesError') === '1';

  if (!isSpainDprUnexportedRedirect) {
    return undefined;
  }

  const payableFilter = searchParams.get('payableFilter');
  const dateRange = extractPayableDateRangeFromPayableFilter(payableFilter);

  if (!dateRange) {
    return undefined;
  }

  const prepareInboxBaseRoute = routeFor(routes.EXPENSE_INBOX_PREPARE.path, {
    company: companyId,
  });
  const exportBaseRoute = routeFor(routes.EXPENSE_INBOX_EXPORT.path, {
    company: companyId,
  });

  const isPayablePrepared = getIsPayablePrepared(payable);

  const href = isPayablePrepared
    ? buildExportRouteFromRange(exportBaseRoute, dateRange)
    : buildPrepareRouteFromRange(prepareInboxBaseRoute, dateRange);

  const i18nKey = isPayablePrepared
    ? 'payables.spanishDPR.unexportedPayablesRedirect.export.title'
    : 'payables.spanishDPR.unexportedPayablesRedirect.prepareAndExport.title';

  return { i18nKey, href };
};

export const buildPrepareRouteFromRange = (
  prepareBaseRoute: string,
  range: PayableDateRange,
): string => {
  const filter: SelectedFilter['filter'] = {
    operator: 'and',
    subfilters: [
      {
        operator: 'and',
        subfilters: [
          {
            field: 'documentaryEvidenceStatus',
            operator: 'is',
            value: ['providedAndDpr'],
          },
          {
            field: 'payableDate',
            operator: 'isBetween',
            value: {
              from: range.from,
              to: range.to,
            },
          },
        ],
      },
    ],
  };
  const fields: Field[] = [
    { key: 'documentaryEvidenceStatus', label: 'Receipt status', kind: 'id' },
    { key: 'payableDate', label: 'Payable date', kind: 'date' },
  ];

  const filterParameter = toFilterUrlParameter(
    { filter, name: '', id: undefined },
    fields,
  );

  return `${prepareBaseRoute}?payableFilter=${filterParameter}`;
};

export const buildExportRouteFromRange = (
  exportBaseRoute: string,
  range: PayableDateRange,
): string => {
  return `${exportBaseRoute}?from=${range.from}&to=${range.to}`;
};
